import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SeoPageLayout from "@/components/seo/SeoPageLayout";
import Breadcrumbs from "@/components/seo/Breadcrumbs";
import CtaBlock from "@/components/seo/CtaBlock";
import useSeo from "@/hooks/useSeo";
import { SERVICES } from "@/data/seo/services";
import { breadcrumbSchema, canonicalFor } from "@/lib/seoHelpers";

export default function ServicesIndex() {
  const canonical = canonicalFor("/services");
  const crumbs = [
    { label: "Home", href: "/" },
    { label: "Services", href: "/services" },
  ];

  useSeo({
    title:
      "Wedding Invitation Services | Luxury, Premium, Digital & More | InvitationWala",
    description:
      "Explore InvitationWala's full range of wedding invitation services — luxury bespoke, premium custom, digital, wedding websites, RSVP systems and stationery.",
    canonical,
  });

  return (
    <SeoPageLayout schemas={[breadcrumbSchema(crumbs)]}>
      <div
        data-testid="services-index"
        className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14 py-6 md:py-12"
      >
        <Breadcrumbs items={crumbs} />

        <div className="mb-14 md:mb-20">
          <span className="overline text-[var(--iw-gold)]">The Atelier</span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif-display text-[var(--iw-ivory)] text-4xl md:text-6xl lg:text-7xl leading-[1.02] mt-4 tracking-tight"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Wedding Invitation Services
          </motion.h1>
          <p className="text-[var(--iw-muted)] text-lg md:text-xl leading-relaxed mt-8 max-w-2xl">
            Twenty services spanning luxury bespoke invitations, wedding websites,
            RSVP systems, printed stationery and destination wedding suites — all
            handcrafted for Indian and NRI couples worldwide.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-3 md:gap-4">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              className="group p-6 md:p-7 transition-all hover:bg-white/5 flex items-start justify-between gap-4"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(212,175,55,0.18)",
              }}
              data-testid={`services-index-link-${s.slug}`}
            >
              <div className="flex-1 min-w-0">
                <span className="overline text-[var(--iw-gold)] block mb-2 text-[10px]">
                  {s.eyebrow}
                </span>
                <h2
                  className="font-serif-display text-[var(--iw-ivory)] text-2xl md:text-3xl leading-tight group-hover:text-[var(--iw-gold)] transition-colors"
                  style={{ fontFamily: "Cormorant Garamond, serif" }}
                >
                  {s.title}
                </h2>
                <p className="text-[var(--iw-muted)] text-sm leading-relaxed mt-3 line-clamp-2">
                  {s.intro.slice(0, 160)}…
                </p>
              </div>
              <ArrowUpRight
                size={18}
                className="text-[var(--iw-faint)] group-hover:text-[var(--iw-gold)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all shrink-0 mt-2"
              />
            </Link>
          ))}
        </div>

        <CtaBlock
          title="Not sure which service is right?"
          subtitle="Send us a WhatsApp message describing your wedding and we'll recommend the right services and share a transparent quote."
          testid="services-index-cta"
        />
      </div>
    </SeoPageLayout>
  );
}
